import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import AIICON from "assets/images/ai.jpeg";
import { motion, AnimatePresence } from "framer-motion";
import artificialInstance from "api/aiInstance";
import useSessions from "hooks/ai/useSessions";
import useCreateSession from "hooks/ai/useCreateSession";
import useSendChatMessage from "hooks/ai/useSendChatMessage";
import useUploadAttachment from "hooks/ai/useUploadAttachment";
import { Eye } from "lucide-react";
import _ from "lodash";
import moment from "moment";
import useUpdateSessionTitle from "hooks/ai/useUpdateSessionTitle";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAppNavigate } from "_graphql/cache/auth";
import useAIChatState from "hooks/ai/useAIChatState";
import { useCurrentUser } from "_graphql/cache/auth";

interface Message {
    id: number;
    role: "user" | "ai";
    text?: string;
    attachment?: string;
    navigateTo?: string;
    doc_id?: string;
    analysis?: any;
    offers?: any;
    suggestions?: string[];
}

interface OfferItem {
    offer_id: number;
    class_of_business: string;
    insured_by: string;
    sum_insured: number;
    currency: string;
    status: string;
    created_at: string;
    participation_count: number;
    my_participation_percentage: number | null;
    my_participation_offer_amount: number | null;
    policy_number: string;
    period_of_insurance_from: string;
    period_of_insurance_to: string;
    premium: number;
    place_it_id: number;
}

const SLASH_COMMANDS = [
    {
        cmd: "/dashboard",
        description: "Go to main dashboard",
        path: "/dashboard",
    },
    {
        cmd: "/cancelled",
        description: "View cancelled offers",
        path: "/dashboard/cancelled-offers",
    },
    {
        cmd: "/renewals",
        description: "View offers to renew",
        path: "/dashboard/offers-to-renew",
    },
    {
        cmd: "/expiring",
        description: "View expiring offers",
        path: "/dashboard/expiring-offers",
    },
    {
        cmd: "/hold-cover",
        description: "View held cover requests",
        path: "/dashboard/held-offers",
    },
    { cmd: "/help", description: "Show all available commands", path: "" },
];

const MANAGER_COMMANDS = [
    {
        cmd: "/dashboard",
        description: "Go to manager dashboard",
        path: "/manager-dashboard",
    },
    {
        cmd: "/cancelled",
        description: "View cancelled offers",
        path: "/manager-dashboard/cancelled-offers",
    },
    {
        cmd: "/renewals",
        description: "View offers to renew",
        path: "/manager-dashboard/offers-to-renew",
    },
    {
        cmd: "/expiring",
        description: "View expiring offers",
        path: "/manager-dashboard/expiring-offers",
    },
    {
        cmd: "/hold-cover",
        description: "View held cover requests",
        path: "/manager-dashboard/held-offers",
    },

    { cmd: "/help", description: "Show all available commands", path: "" },
];

// Markdown renderer with custom styles
const MarkdownContent: React.FC<{
    content: string;
    isTyping?: boolean;
    speed?: number;
}> = ({ content, isTyping = false, speed = 20 }) => {
    const [displayedContent, setDisplayedContent] = useState("");
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        if (isTyping && currentIndex < content.length) {
            const timeout = setTimeout(() => {
                setDisplayedContent((prev) => prev + content[currentIndex]);
                setCurrentIndex((prev) => prev + 1);
            }, speed);
            return () => clearTimeout(timeout);
        } else if (!isTyping) {
            setDisplayedContent(content);
        }
    }, [currentIndex, content, speed, isTyping]);

    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
                // Headings
                h1: ({ node, ...props }) => (
                    <h1 className="text-sm font-bold mb-1 mt-3" {...props} />
                ),
                h2: ({ node, ...props }) => (
                    <h2
                        className="text-[13px] font-bold mb-1.5 mt-2.5"
                        {...props}
                    />
                ),
                h3: ({ node, ...props }) => (
                    <h3
                        className="text-[12px] font-semibold mb-1 mt-2"
                        {...props}
                    />
                ),
                // Paragraphs
                p: ({ node, ...props }) => (
                    <p className="mb-1 last:mb-0" {...props} />
                ),
                // Lists
                ul: ({ node, ...props }) => (
                    <ul
                        className=" ml-4 mb-1 space-y-1 [&>li>p]:inline [&>li>p]:m-0 list-none"
                        {...props}
                    />
                ),
                ol: ({ node, ...props }) => (
                    <ol
                        className="list-decimal ml-4 mb-1 space-y-1 [&>li>p]:inline [&>li>p]:m-0"
                        {...props}
                    />
                ),
                li: ({ node, ...props }) => (
                    <li className="text-[12px] leading-relaxed" {...props} />
                ),
                // Code
                code: ({ node, inline, ...props }: any) =>
                    inline ? (
                        <code
                            className="bg-gray-700 text-gray-100 px-1.5 py-0.5 rounded text-[11px] font-mono"
                            {...props}
                        />
                    ) : (
                        <code
                            className="block bg-gray-700 text-gray-100 p-2 rounded text-[11px] font-mono overflow-x-auto mb-1"
                            {...props}
                        />
                    ),
                pre: ({ node, ...props }) => (
                    <pre
                        className="bg-gray-700 rounded mb-1 overflow-x-auto"
                        {...props}
                    />
                ),
                // Links
                a: ({ node, ...props }) => (
                    <a
                        className="text-blue-400 hover:text-blue-300 underline"
                        target="_blank"
                        rel="noopener noreferrer"
                        {...props}
                    />
                ),
                // Blockquotes
                blockquote: ({ node, ...props }) => (
                    <blockquote
                        className="border-l-2 border-gray-400 pl-3 italic my-2"
                        {...props}
                    />
                ),
                // Strong/Bold
                strong: ({ node, ...props }) => (
                    <strong className="font-bold" {...props} />
                ),
                // Emphasis/Italic
                em: ({ node, ...props }) => (
                    <em className="italic" {...props} />
                ),
                // Horizontal rule
                hr: ({ node, ...props }) => (
                    <hr className="my-3 border-gray-400" {...props} />
                ),
            }}>
            {isTyping ? displayedContent : content}
        </ReactMarkdown>
    );
};

// Component to display analysis data
const AnalysisDisplay: React.FC<{ analysis: any }> = ({ analysis }) => {
    const [expanded, setExpanded] = useState(true);
    const [expandedSections, setExpandedSections] = useState<Set<string>>(
        new Set(["summary"]),
    );

    if (!analysis || !analysis.output) return null;

    const { output } = analysis;

    const toggleSection = (section: string) => {
        setExpandedSections((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(section)) {
                newSet.delete(section);
            } else {
                newSet.add(section);
            }
            return newSet;
        });
    };

    const getSeverityColor = (severity: string) => {
        switch (severity?.toLowerCase()) {
            case "critical":
                return "bg-red-100 text-red-700 border-red-200";
            case "warning":
                return "bg-orange-100 text-orange-700 border-orange-200";
            case "caution":
                return "bg-yellow-100 text-yellow-700 border-yellow-200";
            case "info":
                return "bg-blue-100 text-blue-700 border-blue-200";
            default:
                return "bg-gray-100 text-gray-700 border-gray-200";
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3 space-y-2">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                    <span className="text-[11px] font-semibold text-gray-700">
                        PlaceIT Analysis
                    </span>
                    {output.requires_human_judgment && (
                        <span className="text-[9px] text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-200">
                            Requires Review
                        </span>
                    )}
                </div>
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="text-[10px] text-purple-600 hover:text-purple-700 font-medium">
                    {expanded ? "Collapse" : "Expand"}
                </button>
            </div>

            {expanded && (
                <div className="space-y-2">
                    {/* Executive Summary */}
                    {output.executive_summary && (
                        <div className="bg-purple-50/50 rounded-lg p-2.5 border border-purple-100">
                            <button
                                onClick={() => toggleSection("summary")}
                                className="w-full flex items-center justify-between mb-2">
                                <span className="text-[10px] font-bold text-purple-800">
                                    Executive Summary
                                </span>
                                <motion.svg
                                    animate={{
                                        rotate: expandedSections.has("summary")
                                            ? 0
                                            : -90,
                                    }}
                                    className="w-3 h-3 text-purple-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 9l-7 7-7-7"
                                    />
                                </motion.svg>
                            </button>
                            {expandedSections.has("summary") && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}>
                                    {output.executive_summary.headline && (
                                        <div className="text-[11px] font-semibold text-purple-900 mb-1">
                                            {output.executive_summary.headline}
                                        </div>
                                    )}
                                    {output.executive_summary.body && (
                                        <div className="text-[10px] text-purple-700 leading-relaxed">
                                            {output.executive_summary.body}
                                        </div>
                                    )}
                                </motion.div>
                            )}
                        </div>
                    )}

                    {/* Caution Flags */}
                    {output.caution_flags &&
                        output.caution_flags.length > 0 && (
                            <div className="bg-red-50/50 rounded-lg p-2.5 border border-red-100">
                                <button
                                    onClick={() => toggleSection("cautions")}
                                    className="w-full flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-[10px] font-bold text-red-800">
                                            Caution Flags
                                        </span>
                                        <span className="text-[9px] bg-red-200 text-red-800 px-1.5 py-0.5 rounded-full">
                                            {output.caution_flags.length}
                                        </span>
                                    </div>
                                    <motion.svg
                                        animate={{
                                            rotate: expandedSections.has(
                                                "cautions",
                                            )
                                                ? 0
                                                : -90,
                                        }}
                                        className="w-3 h-3 text-red-600"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 9l-7 7-7-7"
                                        />
                                    </motion.svg>
                                </button>
                                {expandedSections.has("cautions") && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="space-y-2">
                                        {output.caution_flags.map(
                                            (flag: any, idx: number) => (
                                                <div
                                                    key={idx}
                                                    className="bg-white rounded p-2 border border-red-200">
                                                    <div className="space-y-1.5">
                                                        <span className="text-[10px] bg-red-100 text-red-700 px-2 py-0.5 rounded-full border border-red-200 inline-block font-semibold uppercase">
                                                            {flag.type}
                                                        </span>
                                                        <div className="text-[11px] text-red-800 leading-relaxed">
                                                            {flag.reason}
                                                        </div>
                                                        {flag.recommendation && (
                                                            <div className="text-[10px] text-red-600 italic pl-3 border-l-2 border-red-200">
                                                                <span className="font-semibold not-italic">
                                                                    Recommendation:
                                                                </span>{" "}
                                                                {
                                                                    flag.recommendation
                                                                }
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ),
                                        )}
                                    </motion.div>
                                )}
                            </div>
                        )}

                    {/* Risk Observations */}
                    {output.risk_observations &&
                        output.risk_observations.length > 0 && (
                            <div className="bg-blue-50/50 rounded-lg p-2.5 border border-blue-100">
                                <button
                                    onClick={() =>
                                        toggleSection("observations")
                                    }
                                    className="w-full flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-[11px] font-bold text-blue-800">
                                            Risk Observations
                                        </span>
                                        <span className="text-[10px] bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded-full">
                                            {output.risk_observations.length}
                                        </span>
                                    </div>
                                    <motion.svg
                                        animate={{
                                            rotate: expandedSections.has(
                                                "observations",
                                            )
                                                ? 0
                                                : -90,
                                        }}
                                        className="w-3 h-3 text-blue-600"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 9l-7 7-7-7"
                                        />
                                    </motion.svg>
                                </button>
                                {expandedSections.has("observations") && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="space-y-1.5">
                                        {output.risk_observations.map(
                                            (obs: any, idx: number) => (
                                                <div
                                                    key={idx}
                                                    className={`rounded p-2 border ${getSeverityColor(obs.severity)}`}>
                                                    <div className="flex items-start gap-2">
                                                        <span
                                                            className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded flex-shrink-0 ${getSeverityColor(obs.severity)}`}>
                                                            {obs.severity}
                                                        </span>
                                                        <div className="text-[10px] leading-relaxed">
                                                            {obs.text}
                                                        </div>
                                                    </div>
                                                </div>
                                            ),
                                        )}
                                    </motion.div>
                                )}
                            </div>
                        )}

                    {/* Strategic Insight */}
                    {output.strategic_insight && (
                        <div className="bg-green-50/50 rounded-lg p-2.5 border border-green-100">
                            <button
                                onClick={() => toggleSection("strategic")}
                                className="w-full flex items-center justify-between mb-2">
                                <span className="text-[10px] font-bold text-green-800">
                                    Strategic Insight
                                </span>
                                <motion.svg
                                    animate={{
                                        rotate: expandedSections.has(
                                            "strategic",
                                        )
                                            ? 0
                                            : -90,
                                    }}
                                    className="w-3 h-3 text-green-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 9l-7 7-7-7"
                                    />
                                </motion.svg>
                            </button>
                            {expandedSections.has("strategic") && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="text-[10px] text-green-700 leading-relaxed">
                                    {output.strategic_insight}
                                </motion.div>
                            )}
                        </div>
                    )}

                    {/* Underwriting Questions */}
                    {output.analysis?.underwriting_questions &&
                        output.analysis.underwriting_questions.length > 0 && (
                            <div className="bg-amber-50/50 rounded-lg p-2.5 border border-amber-100">
                                <button
                                    onClick={() => toggleSection("questions")}
                                    className="w-full flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-[11px] font-bold text-amber-800">
                                            Underwriting Questions
                                        </span>
                                        <span className="text-[10px] bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full">
                                            {
                                                output.analysis
                                                    .underwriting_questions
                                                    .length
                                            }
                                        </span>
                                    </div>
                                    <motion.svg
                                        animate={{
                                            rotate: expandedSections.has(
                                                "questions",
                                            )
                                                ? 0
                                                : -90,
                                        }}
                                        className="w-3 h-3 text-amber-600"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 9l-7 7-7-7"
                                        />
                                    </motion.svg>
                                </button>
                                {expandedSections.has("questions") && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="space-y-2 max-h-64 overflow-y-auto">
                                        {output.analysis.underwriting_questions.map(
                                            (q: any, idx: number) => (
                                                <div
                                                    key={idx}
                                                    className="bg-white rounded p-2 border border-amber-200">
                                                    <div className="text-[11px] font-medium text-amber-900 mb-1">
                                                        {idx + 1}. {q.question}
                                                    </div>
                                                    {q.rationale && (
                                                        <div className="text-[10px] text-amber-700 italic">
                                                            {q.rationale}
                                                        </div>
                                                    )}
                                                    {q.topic && (
                                                        <span className="inline-block mt-1 text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                                            {q.topic}
                                                        </span>
                                                    )}
                                                </div>
                                            ),
                                        )}
                                    </motion.div>
                                )}
                            </div>
                        )}

                    {/* Required Documents */}
                    {output.analysis?.required_documents && (
                        <div className="bg-indigo-50/50 rounded-lg p-2.5 border border-indigo-100">
                            <button
                                onClick={() => toggleSection("documents")}
                                className="w-full flex items-center justify-between mb-2">
                                <span className="text-[11px] font-bold text-indigo-800">
                                    Required Documents
                                </span>
                                <motion.svg
                                    animate={{
                                        rotate: expandedSections.has(
                                            "documents",
                                        )
                                            ? 0
                                            : -90,
                                    }}
                                    className="w-3 h-3 text-indigo-600"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 9l-7 7-7-7"
                                    />
                                </motion.svg>
                            </button>
                            {expandedSections.has("documents") && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="space-y-2">
                                    {output.analysis.required_documents
                                        .mandatory &&
                                        output.analysis.required_documents
                                            .mandatory.length > 0 && (
                                            <div>
                                                <div className="text-[10px] font-semibold text-red-700 mb-1">
                                                    Mandatory:
                                                </div>
                                                <ul className="space-y-1">
                                                    {output.analysis.required_documents.mandatory.map(
                                                        (
                                                            doc: string,
                                                            idx: number,
                                                        ) => (
                                                            <li
                                                                key={idx}
                                                                className="text-[10px] text-indigo-700 flex items-start gap-1.5">
                                                                <span className="text-red-500 flex-shrink-0">
                                                                    •
                                                                </span>
                                                                <span>
                                                                    {doc}
                                                                </span>
                                                            </li>
                                                        ),
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                    {output.analysis.required_documents
                                        .recommended &&
                                        output.analysis.required_documents
                                            .recommended.length > 0 && (
                                            <div>
                                                <div className="text-[10px] font-semibold text-amber-700 mb-1">
                                                    Recommended:
                                                </div>
                                                <ul className="space-y-1">
                                                    {output.analysis.required_documents.recommended.map(
                                                        (
                                                            doc: string,
                                                            idx: number,
                                                        ) => (
                                                            <li
                                                                key={idx}
                                                                className="text-[10px] text-indigo-700 flex items-start gap-1.5">
                                                                <span className="text-amber-500 flex-shrink-0">
                                                                    •
                                                                </span>
                                                                <span>
                                                                    {doc}
                                                                </span>
                                                            </li>
                                                        ),
                                                    )}
                                                </ul>
                                            </div>
                                        )}
                                </motion.div>
                            )}
                        </div>
                    )}

                    {/* Disclaimer */}
                    {output.disclaimer && (
                        <div className="bg-gray-50 rounded-lg p-2 border border-gray-200">
                            <div className="text-[9px] text-gray-600 italic flex items-start gap-1.5">
                                <svg
                                    className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                                <span>{output.disclaimer.text}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </motion.div>
    );
};

// Component to display offer data
const OfferDisplay: React.FC<{
    offers: any;
    navigate: ReturnType<typeof useAppNavigate>;
    isManager: boolean;
}> = ({ offers, navigate, isManager }) => {
    const [expanded, setExpanded] = useState(true);

    if (!offers || !offers.items || offers.items.length === 0) return null;

    const getStatusColor = (status: string) => {
        switch (status.toUpperCase()) {
            case "OPEN":
                return "bg-green-100 text-green-700 border-green-200";
            case "CLOSED":
                return "bg-gray-100 text-gray-700 border-gray-200";
            case "PENDING":
                return "bg-yellow-100 text-yellow-700 border-yellow-200";
            case "CANCELLED":
                return "bg-red-100 text-red-700 border-red-200";
            default:
                return "bg-blue-100 text-blue-700 border-blue-200";
        }
    };

    const formatCurrency = (amount: number, currency: string) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency || "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3 space-y-2">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    <span className="text-[11px] font-semibold text-gray-700">
                        {offers.items.length} Offer
                        {offers.items.length !== 1 ? "s" : ""} Found
                    </span>
                    {offers.truncated && (
                        <span className="text-[10px] text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-200">
                            Showing first {offers.items.length}
                        </span>
                    )}
                </div>
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="text-[10px] text-blue-600 hover:text-blue-700 font-medium">
                    {expanded ? "Collapse" : "Expand"}
                </button>
            </div>

            {/* Filters Applied */}
            {offers.filters_applied && expanded && (
                <div className="bg-blue-50/50 rounded-lg p-2 border border-blue-100">
                    <div className="text-[10px] font-semibold text-blue-700 mb-1">
                        Filters Applied:
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {Object.entries(offers.filters_applied).map(
                            ([key, value]: [string, any]) => {
                                if (value && value !== "any") {
                                    return (
                                        <span
                                            key={key}
                                            className="text-[9px] bg-white text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                                            {key.replace(/_/g, " ")}:{" "}
                                            {typeof value === "object"
                                                ? JSON.stringify(value)
                                                : value}
                                        </span>
                                    );
                                }
                                return null;
                            },
                        )}
                        {Object.values(offers.filters_applied).every(
                            (v: any) => !v || v === "any",
                        ) && (
                            <span className="text-[9px] text-blue-600">
                                No filters applied
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Offers List */}
            {expanded && (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                    {offers.items.map((offer: OfferItem, index: number) => (
                        <motion.div
                            key={offer.offer_id}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="bg-white rounded-lg border border-gray-200 p-2.5 hover:shadow-md transition-all cursor-pointer">
                            {/* Header Row */}
                            <div className="flex items-start justify-between mb-1">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-[11px] font-bold text-gray-800">
                                            {offer.insured_by}
                                        </span>
                                        <span
                                            className={`text-[9px] font-medium px-2 py-0.5 rounded-full border ${getStatusColor(offer.status)}`}>
                                            {offer.status}
                                        </span>
                                    </div>
                                    <div className="text-[10px] text-gray-500">
                                        {offer.policy_number} •{" "}
                                        {offer.class_of_business}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[11px] font-bold text-blue-600">
                                        {formatCurrency(
                                            offer.sum_insured,
                                            offer.currency,
                                        )}
                                    </div>
                                    <div className="text-[9px] text-gray-500">
                                        Premium:{" "}
                                        {formatCurrency(
                                            offer.premium,
                                            offer.currency,
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Details Row */}
                            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                                <div>
                                    <div className="text-[9px] text-gray-500 mb-0.5">
                                        Period
                                    </div>
                                    <div className="text-[10px] text-gray-700 font-medium">
                                        {formatDate(
                                            offer.period_of_insurance_from,
                                        )}{" "}
                                        -{" "}
                                        {formatDate(
                                            offer.period_of_insurance_to,
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[9px] text-gray-500 mb-0.5">
                                        Participation
                                    </div>
                                    <div className="text-[10px] text-gray-700 font-medium">
                                        {offer.participation_count > 0 ? (
                                            <span className="text-green-600">
                                                {offer.participation_count}{" "}
                                                reinsurer
                                                {offer.participation_count !== 1
                                                    ? "s"
                                                    : ""}
                                            </span>
                                        ) : (
                                            <span className="text-orange-600">
                                                None yet
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="col-span-2 flex items-center justify-end mt-2">
                                    <button
                                        onClick={() => {
                                            const basePath = isManager
                                                ? "/manager-dashboard"
                                                : "/dashboard";
                                            navigate({
                                                to: `${basePath}/offers/${offer.place_it_id}?_content=${offer.place_it_id}`,
                                            });
                                        }}>
                                        <Eye className="w-4 h-4 text-blue-600" />
                                    </button>
                                </div>
                            </div>

                            {/* My Participation */}
                            {offer.my_participation_percentage && (
                                <div className="mt-2 bg-blue-50 rounded p-1.5 border border-blue-100">
                                    <div className="text-[9px] text-blue-700">
                                        Your participation:{" "}
                                        <span className="font-bold">
                                            {offer.my_participation_percentage}%
                                        </span>
                                        {offer.my_participation_offer_amount && (
                                            <>
                                                {" "}
                                                •{" "}
                                                {formatCurrency(
                                                    offer.my_participation_offer_amount,
                                                    offer.currency,
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    ))}
                </div>
            )}
        </motion.div>
    );
};

export const AIChatPanel: React.FC = () => {
    const currentUser = useCurrentUser();
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [showCommandPalette, setShowCommandPalette] = useState(false);
    const [commandFilter, setCommandFilter] = useState("");
    const [typingMessageId, setTypingMessageId] = useState<number | null>(0);
    const [isListening, setIsListening] = useState(false);

    const [showSessionHistory, setShowSessionHistory] = useState(false);
    const [uploadedDocIds, setUploadedDocIds] = useState<string[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [isCreatingSession, setIsCreatingSession] = useState(false);
    const [loadedSession, setLoadedSession] = useState<any>(null);
    const [currentSessionId, setCurrentSessionIdState] = useState<string | null>(
        () => localStorage.getItem("__a_session_id"),
    );
    const [collapsedMonths, setCollapsedMonths] = useState<Set<string>>(
        new Set(),
    );

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const recognitionRef = useRef<any>(null);
    const finalTranscriptRef = useRef<string>("");

    const { openAIChat, toggleAIChat } = useAIChatState();
    const { updateSessionTitle } = useUpdateSessionTitle();
    const navigate = useAppNavigate();
    const user = {} as any;

    const VINCENT_DESCRIPTION =
        "Hello I am Vincent, your intelligent assistant built right into placeIT. I can help you navigate the platform, find offers, and assist you with any reinsurance related tasks. You can interact with me using simple commands or natural conversation. I also support voice input, making it even easier to get help while you work.";

    const SUGGESTIONS_TRANSLATED = useMemo(
        () => [
            "Show me my dashboard",
            "View cancelled offers",
            "Check expiring offers",
            "View my profile",
        ],
        [],
    );

    const setCurrentSessionId = (sessionId: string | null) => {
        if (sessionId) {
            localStorage.setItem("__a_session_id", sessionId);
        } else {
            localStorage.removeItem("__a_session_id");
        }
        setCurrentSessionIdState(sessionId);
    };

    const pendingSendRef = useRef(false);

    const toggleMonthCollapse = (month: string) => {
        setCollapsedMonths((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(month)) {
                newSet.delete(month);
            } else {
                newSet.add(month);
            }
            return newSet;
        });
    };
    // Hooks for AI backend
    const {
        sessions,
        refetch: refetchSessions,
    } = useSessions({
        brokerId: Number(
            currentUser?.employee?.broker_config?.broker_configuration_id,
        ),
    });

    const groupedSessions = useMemo(() => {
        if (!sessions || sessions.length === 0) return {};

        return _.groupBy(sessions, (session: any) => {
            return moment(session.created_at).format("MMMM YYYY");
        });
    }, [sessions]);

    const { createSession } = useCreateSession({
        brokerId: Number(
            currentUser?.employee?.broker_config?.broker_configuration_id,
        ),
    });
    const { sendMessage: sendAIMessage, isLoading: isSendingMessage } =
        useSendChatMessage();
    const { uploadAttachment, isUploading } = useUploadAttachment();

    const isManager = window.location.pathname.includes("manager-dashboard");
    const activeCommands = isManager ? MANAGER_COMMANDS : SLASH_COMMANDS;

    const loadSessionHistory = useCallback(async (sessionId: string) => {
        if (pendingSendRef.current) return;

        setIsLoadingHistory(true);
        try {
            const result = await artificialInstance.get(
                `v1/sessions/${sessionId}`,
            );
            const sessionData = result?.data;
            setLoadedSession(sessionData);

            if (sessionData?.turns?.length > 0) {
                const loadedMessages: Message[] = sessionData.turns.map(
                    (turn: any, index: number) => ({
                        id: index,
                        role: turn.role === "user" ? "user" : "ai",
                        text: turn.content,
                        analysis: turn.analysis,
                        offers: turn.offers,
                        suggestions: turn.suggestions,
                    }),
                );
                setMessages(loadedMessages);
            } else {
                setMessages([]);
            }
        } catch (error) {
            console.error("Failed to load session history:", error);
            setMessages([]);
        } finally {
            setIsLoadingHistory(false);
        }
    }, []);

    const loadSession = (sessionId: string) => {
        setCurrentSessionId(sessionId);
        setShowSessionHistory(false);
        loadSessionHistory(sessionId);
    };

    useEffect(() => {
        if (openAIChat) {
            refetchSessions();
            if (currentSessionId) {
                loadSessionHistory(currentSessionId);
            } else {
                setMessages([]);
                setLoadedSession(null);
            }
        }
    }, [openAIChat]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "end",
        });
    }, [messages, isTyping]);

    useEffect(() => {
        // Initialize speech recognition
        const SpeechRecognition =
            (window as any).SpeechRecognition ||
            (window as any).webkitSpeechRecognition;

        if (SpeechRecognition) {
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = "en-US";

            recognition.onresult = (event: any) => {
                let interimTranscript = "";
                let finalTranscript = "";

                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript + " ";
                    } else {
                        interimTranscript += transcript;
                    }
                }

                if (finalTranscript) {
                    finalTranscriptRef.current += finalTranscript;
                }

                // Update input with accumulated final text + current interim text
                setInput(finalTranscriptRef.current + interimTranscript);
            };

            recognition.onerror = (event: any) => {
                console.error("Speech recognition error:", event.error);
                setIsListening(false);
            };

            recognition.onend = () => {
                setIsListening(false);
            };

            recognitionRef.current = recognition;
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, []);

    const handleInputChange = (value: string) => {
        setInput(value);
        // If manually typing, update the base transcript
        if (!isListening) {
            finalTranscriptRef.current = value;
        }
        if (value.startsWith("/")) {
            setShowCommandPalette(true);
            setCommandFilter(value.slice(1).toLowerCase());
        } else {
            setShowCommandPalette(false);
            setCommandFilter("");
        }
    };

    const toggleSpeechRecognition = () => {
        if (!recognitionRef.current) {
            alert(
                "Speech recognition is not supported in your browser. Please use Chrome or Edge.",
            );
            return;
        }

        if (isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
        } else {
            try {
                // Store the current input as the base for new transcription
                finalTranscriptRef.current = input;
                recognitionRef.current.start();
                setIsListening(true);
            } catch (error) {
                console.error("Error starting speech recognition:", error);
            }
        }
    };

    const filteredCommands = activeCommands.filter(
        (c) =>
            c.cmd.slice(1).includes(commandFilter) ||
            c.description.toLowerCase().includes(commandFilter),
    );

    const sendMessage = async (text: string) => {
        if (
            !text.trim() ||
            !user ||
            isCreatingSession ||
            isSendingMessage ||
            pendingSendRef.current
        )
            return;

        setShowCommandPalette(false);

        const attachmentDocIds = [...uploadedDocIds];

        const userMsg: Message = {
            id: Date.now(),
            role: "user",
            text,
        };
        pendingSendRef.current = true;
        setMessages((prev) => [...prev, userMsg]);
        setInput("");
        finalTranscriptRef.current = "";
        setUploadedDocIds([]);
        setIsTyping(true);

        try {
            let sessionId = currentSessionId;
            if (!sessionId) {
                setIsCreatingSession(true);
                const result = await createSession();
                sessionId =
                    result.data?.createAgentSessionId?.session?.session_id ??
                    null;
                if (!sessionId) {
                    throw new Error("Failed to create session");
                }
                setCurrentSessionId(sessionId);
                await refetchSessions();
                setIsCreatingSession(false);
            }

            const response = await sendAIMessage({
                message: {
                    role: "user",
                    content: text,
                },
                attachment_doc_ids:
                    attachmentDocIds.length > 0
                        ? (attachmentDocIds as any)
                        : undefined,
            });

            const aiMessageId = Date.now() + 1;
            setTypingMessageId(aiMessageId);
            setMessages((prev) => [
                ...prev,
                {
                    id: aiMessageId,
                    role: "ai",
                    text: response.reply,
                    analysis: response.analysis,
                    offers: response.offers,
                    suggestions: response.suggestions,
                },
            ]);

            if (response.session_title) {
                const refreshed = await refetchSessions();
                const refreshedSessions =
                    refreshed.data?.fetchAgentSessionIds?.session_ids ?? [];
                const insurerId = refreshedSessions.find(
                    (s: any) => s.session_id === sessionId,
                )?.id;
                if (insurerId) {
                    await updateSessionTitle({
                        sessionId: insurerId,
                        title: response.session_title,
                    });
                }
            }
        } catch (error) {
            console.error("Failed to send message:", error);
            const errorId = Date.now() + 1;
            setMessages((prev) => [
                ...prev,
                {
                    id: errorId,
                    role: "ai",
                    text: "I apologize, but I encountered an error processing your request. Please try again.",
                },
            ]);
        } finally {
            pendingSendRef.current = false;
            setIsCreatingSession(false);
            setIsTyping(false);
        }
    };

    const handleFileAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !currentSessionId) return;

        try {
            const result = await uploadAttachment(file);
            setUploadedDocIds((prev) => [...prev, result.doc_id]);

            // Show attachment in UI
            const attachMsg: Message = {
                id: Date.now(),
                role: "user",
                text: `📎 Attached: ${file.name}`,
                attachment: file.name,
                doc_id: result.doc_id,
            };
            setMessages((prev) => [...prev, attachMsg]);
        } catch (error) {
            console.error("Failed to upload attachment:", error);
            alert("Failed to upload file. Please try again.");
        } finally {
            e.target.value = "";
        }
    };

    const startNewChat = () => {
        if (!user) return;

        setCurrentSessionId(null);
        setMessages([]);
        setLoadedSession(null);
        setInput("");
        finalTranscriptRef.current = "";
        setUploadedDocIds([]);
        setIsTyping(false);
        setShowCommandPalette(false);
        setShowSessionHistory(false);
    };

    const downloadChat = () => {
        const lines = messages.map(
            (m) =>
                `[${m.role === "ai" ? "Vincent AI" : "You"}]\n${m.text}${m.attachment ? `\n[File: ${m.attachment}]` : ""}`,
        );
        const blob = new Blob([lines.join("\n\n---\n\n")], {
            type: "text/plain",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `vincent-chat-${new Date().toISOString().slice(0, 10)}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div
            className={`overflow-hidden h-screen  bg-white flex flex-col shadow-xl border-l border-gray-200  ${openAIChat ? "translate-x-0 max-w-[520px] min-w-96" : "translate-x-full w-0"} transition-transform duration-300 fixed  right-0 top-0 z-40`}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-[#2e5cb8] flex-shrink-0">
                <div className="flex items-center gap-2.5">
                    <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-white to-blue-400 shadow-sm">
                        <img
                            src={AIICON}
                            alt="placeIT Logo"
                            className="h-7 w-auto rounded-full"
                        />
                    </div>
                    <div>
                        <h2 className="text-sm font-semibold text-gray-900">
                            Ask Vincent
                        </h2>
                        <div className="flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-700 animate-pulse" />
                            <span className="text-xs text-blue-900">
                                {currentSessionId ? "Session Active" : "Ready"}
                            </span>
                        </div>
                    </div>
                </div>
                {/* Header actions */}
                <div className="flex items-center gap-1">
                    <motion.button
                        whileHover={{ scale: 1.1, rotate: 90 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={startNewChat}
                        title={"New Chat"}
                        className="p-1.5 text-white hover:text-blue-600 rounded-lg hover:bg-white/70 transition-all">
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4v16m8-8H4"
                            />
                        </svg>
                    </motion.button>
                    <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() =>
                            setShowSessionHistory(!showSessionHistory)
                        }
                        title={"Chat history"}
                        className="p-1.5 text-white hover:text-blue-600 rounded-lg hover:bg-white/70 transition-all">
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                    </motion.button>
                    <motion.button
                        whileHover={{ scale: 1.1, y: -2 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={downloadChat}
                        title={"Download chat"}
                        className="p-1.5 text-white hover:text-blue-600 rounded-lg hover:bg-white/70 transition-all">
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                            />
                        </svg>
                    </motion.button>
                    <motion.button
                        whileHover={{ scale: 1.1, rotate: 90 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={toggleAIChat}
                        title={"Close"}
                        className="p-1.5 text-white hover:text-gray-600 rounded-lg hover:bg-white/70 transition-all">
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </motion.button>
                </div>
            </div>

            {/* Session History Modal */}
            <AnimatePresence>
                {showSessionHistory && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/50 z-50"
                        onClick={() => setShowSessionHistory(false)}>
                        <motion.div
                            initial={{ x: -300 }}
                            animate={{ x: 0 }}
                            exit={{ x: -300 }}
                            onClick={(e) => e.stopPropagation()}
                            className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-2xl overflow-y-auto pt-20 lg:pt-0">
                            <div className="sticky top-0 bg-gradient-to-r from-blue-50 to-[#2e5cb8] px-4 py-3 border-b border-gray-200">
                                <h3 className="text-sm font-semibold text-gray-900">
                                    {"Chat History"}
                                </h3>
                            </div>
                            <div className="p-3 space-y-3">
                                {sessions && sessions?.length > 0 ? (
                                    Object.entries(groupedSessions).map(
                                        ([month, monthSessions]: [
                                            string,
                                            any,
                                        ]) => {
                                            const isCollapsed =
                                                !collapsedMonths.has(month);
                                            return (
                                                <div key={month}>
                                                    {/* Month Header */}
                                                    <motion.button
                                                        onClick={() =>
                                                            toggleMonthCollapse(
                                                                month,
                                                            )
                                                        }
                                                        className="w-full flex items-center justify-between px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                                        whileHover={{
                                                            scale: 1.01,
                                                        }}
                                                        whileTap={{
                                                            scale: 0.99,
                                                        }}>
                                                        <span className="text-xs font-semibold text-gray-700">
                                                            {month}
                                                        </span>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[10px] text-gray-500 bg-white px-2 py-0.5 rounded-full">
                                                                {
                                                                    monthSessions.length
                                                                }
                                                            </span>
                                                            <motion.svg
                                                                animate={{
                                                                    rotate: isCollapsed
                                                                        ? -90
                                                                        : 0,
                                                                }}
                                                                transition={{
                                                                    duration: 0.2,
                                                                }}
                                                                className="w-4 h-4 text-gray-600"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24">
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    d="M19 9l-7 7-7-7"
                                                                />
                                                            </motion.svg>
                                                        </div>
                                                    </motion.button>

                                                    {/* Month Sessions */}
                                                    <AnimatePresence
                                                        initial={false}>
                                                        {!isCollapsed && (
                                                            <motion.div
                                                                initial={{
                                                                    height: 0,
                                                                    opacity: 0,
                                                                }}
                                                                animate={{
                                                                    height: "auto",
                                                                    opacity: 1,
                                                                }}
                                                                exit={{
                                                                    height: 0,
                                                                    opacity: 0,
                                                                }}
                                                                transition={{
                                                                    duration: 0.2,
                                                                }}
                                                                className="overflow-hidden space-y-2 mt-2">
                                                                {monthSessions.map(
                                                                    (
                                                                        sess: any,
                                                                        index: number,
                                                                    ) => (
                                                                        <motion.div
                                                                            key={
                                                                                sess.session_id +
                                                                                index
                                                                            }
                                                                            initial={{
                                                                                opacity: 0,
                                                                                x: -10,
                                                                            }}
                                                                            animate={{
                                                                                opacity: 1,
                                                                                x: 0,
                                                                            }}
                                                                            transition={{
                                                                                delay:
                                                                                    index *
                                                                                    0.05,
                                                                            }}
                                                                            whileHover={{
                                                                                scale: 1.02,
                                                                            }}
                                                                            className={`px-3 py-1 border-b cursor-pointer transition-all ${
                                                                                sess.session_id ===
                                                                                currentSessionId
                                                                                    ? "bg-blue-50 border-blue-300"
                                                                                    : "bg-white border-gray-200 hover:bg-gray-50"
                                                                            }`}
                                                                            onClick={() =>
                                                                                loadSession(
                                                                                    sess.session_id,
                                                                                )
                                                                            }>
                                                                            {/* Title */}
                                                                            <div className="text-xs font-semibold text-gray-800 mb-1.5 line-clamp-2">
                                                                                {sess?.session_title ||
                                                                                    "Untitled Session"}
                                                                            </div>

                                                                            <div className="flex items-center justify-between">
                                                                                {/* Associate Name */}
                                                                                {sess?.insurer_associate && (
                                                                                    <div className="flex items-center gap-1.5 mb-1">
                                                                                        <svg
                                                                                            className="w-3 h-3 text-gray-400 flex-shrink-0"
                                                                                            fill="none"
                                                                                            stroke="currentColor"
                                                                                            viewBox="0 0 24 24">
                                                                                            <path
                                                                                                strokeLinecap="round"
                                                                                                strokeLinejoin="round"
                                                                                                strokeWidth={
                                                                                                    2
                                                                                                }
                                                                                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                                                                            />
                                                                                        </svg>
                                                                                        <span className="text-[10px] text-gray-600">
                                                                                            {
                                                                                                sess
                                                                                                    .insurer_associate
                                                                                                    .assoc_first_name
                                                                                            }{" "}
                                                                                            {
                                                                                                sess
                                                                                                    .insurer_associate
                                                                                                    .assoc_last_name
                                                                                            }
                                                                                        </span>
                                                                                    </div>
                                                                                )}

                                                                                {/* Time ago */}
                                                                                <div className="flex items-center justify-between">
                                                                                    <div className="flex items-center gap-1.5">
                                                                                        <svg
                                                                                            className="w-3 h-3 text-gray-400 flex-shrink-0"
                                                                                            fill="none"
                                                                                            stroke="currentColor"
                                                                                            viewBox="0 0 24 24">
                                                                                            <path
                                                                                                strokeLinecap="round"
                                                                                                strokeLinejoin="round"
                                                                                                strokeWidth={
                                                                                                    2
                                                                                                }
                                                                                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                                                            />
                                                                                        </svg>
                                                                                        <span className="text-[10px] text-gray-500">
                                                                                            {moment(
                                                                                                sess.updated_at ||
                                                                                                    sess.created_at,
                                                                                            ).fromNow()}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </motion.div>
                                                                    ),
                                                                )}
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                            );
                                        },
                                    )
                                ) : (
                                    <div className="text-xs text-gray-500 text-center py-4">
                                        No previous sessions
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Messages */}
            <div className="h-[86vh] overflow-scroll overflow-y-scroll bg-gray-50 px-4 py-4 space-y-4">
                {isLoadingHistory && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex flex-col items-center justify-center py-12">
                        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-3" />
                        <div className="text-sm font-medium text-gray-700">
                            Loading conversation history...
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            Retrieving your session data
                        </div>
                    </motion.div>
                )}

                {!isLoadingHistory && messages.length === 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35 }}
                        className="flex flex-col items-center justify-center min-h-full py-12 px-4 text-center">
                        <motion.div
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 200 }}
                            className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-[#2e5cb8] flex items-center justify-center shadow-md mb-5">
                            <img
                                src={AIICON}
                                alt="Vincent"
                                className="h-10 w-10 rounded-xl object-cover"
                            />
                        </motion.div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            How can I help you today?
                        </h3>
                        <p className="text-xs text-gray-500 max-w-sm leading-relaxed mb-6">
                            {VINCENT_DESCRIPTION}
                        </p>
                        <div className="flex flex-wrap gap-2 justify-center max-w-md">
                            {SUGGESTIONS_TRANSLATED.map((s, index) => (
                                <motion.button
                                    key={s}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.1 + index * 0.05 }}
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.97 }}
                                    onClick={() => sendMessage(s)}
                                    disabled={
                                        isCreatingSession || isSendingMessage
                                    }
                                    className="text-xs px-3 py-1.5 rounded-full bg-white text-blue-700 border border-blue-100 hover:bg-blue-50 hover:border-blue-200 transition-colors disabled:opacity-50">
                                    {s}
                                </motion.button>
                            ))}
                        </div>
                    </motion.div>
                )}

                {!isLoadingHistory && messages.length > 0 && (
                    <>
                        {currentSessionId &&
                            loadedSession &&
                            messages.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 mb-1">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <svg
                                                className="w-4 h-4 text-blue-600"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                            <div>
                                                <div className="text-[11px] font-semibold text-blue-800">
                                                    Conversation History Loaded
                                                </div>
                                                <div className="text-[10px] text-blue-600">
                                                    {messages.length} message
                                                    {messages.length !== 1
                                                        ? "s"
                                                        : ""}{" "}
                                                    • Last updated:{" "}
                                                    {new Date(
                                                        loadedSession?.udated_at ||
                                                            loadedSession?.created_at,
                                                    ).toLocaleString("en-US", {
                                                        month: "short",
                                                        day: "numeric",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                        <AnimatePresence initial={false}>
                            {messages?.map((msg, index) => (
                            <motion.div
                                key={msg.id}
                                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 260,
                                    damping: 20,
                                    delay:
                                        index === messages.length - 1 ? 0 : 0,
                                }}
                                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                                {msg.role === "ai" && (
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{
                                            delay: 0.1,
                                            type: "spring",
                                            stiffness: 200,
                                        }}
                                        className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-[#2e5cb8] flex-shrink-0 flex items-center justify-center mr-2 mt-0.5">
                                        <img
                                            src={AIICON}
                                            alt="placeIT Logo"
                                            className="h-5 w-auto rounded-full"
                                        />
                                    </motion.div>
                                )}
                                <motion.div
                                    initial={{ scale: 0.9 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.05 }}
                                    className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 text-[12px] text-gray-700 whitespace-pre-line ${
                                        msg.role === "user"
                                            ? "bg-gradient-to-br from-blue-500 to-[#2e5cb8] text-white rounded-tr-sm"
                                            : "bg-slate-200 text-gray-500 rounded-tl-sm"
                                    }`}>
                                    {msg.text && (
                                        <div className="markdown-wrapper">
                                            {msg.role === "ai" ? (
                                                <MarkdownContent
                                                    content={msg.text}
                                                    isTyping={
                                                        msg.id ===
                                                        typingMessageId
                                                    }
                                                    speed={20}
                                                />
                                            ) : (
                                                msg.text
                                            )}
                                        </div>
                                    )}
                                    {msg.attachment && (
                                        <div
                                            className={`mt-1.5 flex items-center gap-1.5 text-xs ${msg.role === "user" ? "text-white/80" : "text-gray-500"}`}>
                                            <svg
                                                className="w-3.5 h-3.5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                                                />
                                            </svg>
                                            {msg.attachment}
                                        </div>
                                    )}
                                    {msg.navigateTo && msg.role === "ai" && (
                                        <motion.button
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.5 }}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() =>
                                                navigate({
                                                    to: msg.navigateTo!,
                                                })
                                            }
                                            className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white rounded-lg text-xs font-medium hover:bg-blue-600 transition-colors">
                                            <svg
                                                className="w-3.5 h-3.5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                                                />
                                            </svg>
                                            Go to page
                                        </motion.button>
                                    )}
                                    {msg.analysis && msg.role === "ai" && (
                                        <AnalysisDisplay
                                            analysis={msg.analysis}
                                        />
                                    )}
                                    {msg.offers && msg.role === "ai" && (
                                        <OfferDisplay
                                            offers={msg.offers}
                                            navigate={navigate}
                                            isManager={isManager}
                                        />
                                    )}
                                    {msg.suggestions &&
                                        msg.suggestions.length > 0 &&
                                        msg.role === "ai" && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.3 }}
                                                className="mt-3 space-y-1.5">
                                                <div className="text-[10px] font-semibold text-gray-600 mb-1.5">
                                                    💡 Suggestions :
                                                </div>
                                                {msg.suggestions.map(
                                                    (suggestion, idx) => (
                                                        <motion.button
                                                            key={idx}
                                                            initial={{
                                                                opacity: 0,
                                                                x: -10,
                                                            }}
                                                            animate={{
                                                                opacity: 1,
                                                                x: 0,
                                                            }}
                                                            transition={{
                                                                delay:
                                                                    0.4 +
                                                                    idx * 0.1,
                                                            }}
                                                            whileHover={{
                                                                scale: 1.02,
                                                                x: 2,
                                                            }}
                                                            whileTap={{
                                                                scale: 0.98,
                                                            }}
                                                            onClick={() =>
                                                                sendMessage(
                                                                    suggestion,
                                                                )
                                                            }
                                                            className="w-full text-left text-[11px] px-3 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all flex items-center gap-2">
                                                            <svg
                                                                className="w-3 h-3 flex-shrink-0"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24">
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth={
                                                                        2
                                                                    }
                                                                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                                                />
                                                            </svg>
                                                            {suggestion}
                                                        </motion.button>
                                                    ),
                                                )}
                                            </motion.div>
                                        )}
                                </motion.div>
                            </motion.div>
                        ))}
                        </AnimatePresence>

                        <AnimatePresence>
                            {isTyping && (
                                <div className="flex justify-start items-center gap-2">
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        transition={{ duration: 0.2 }}
                                        className="flex justify-start">
                                        <motion.div
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{
                                                delay: 0.1,
                                                type: "spring",
                                            }}
                                            className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-[#2e5cb8] flex-shrink-0 flex items-center justify-center mr-2 mt-0.5">
                                            <svg
                                                className="w-3 h-3 text-white"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                                                />
                                            </svg>
                                        </motion.div>
                                        <motion.div
                                            initial={{ scale: 0.8 }}
                                            animate={{ scale: 1 }}
                                            className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1">
                                            <motion.span
                                                animate={{ y: [0, -3, 0] }}
                                                transition={{
                                                    duration: 0.6,
                                                    repeat: Infinity,
                                                    repeatType: "loop",
                                                }}
                                                className="w-1.5 h-1.5 bg-gray-400 rounded-full"
                                            />
                                            <motion.span
                                                animate={{ y: [0, -3, 0] }}
                                                transition={{
                                                    duration: 0.6,
                                                    repeat: Infinity,
                                                    repeatType: "loop",
                                                    delay: 0.2,
                                                }}
                                                className="w-1.5 h-1.5 bg-gray-400 rounded-full"
                                            />
                                            <motion.span
                                                animate={{ y: [0, -3, 0] }}
                                                transition={{
                                                    duration: 0.6,
                                                    repeat: Infinity,
                                                    repeatType: "loop",
                                                    delay: 0.4,
                                                }}
                                                className="w-1.5 h-1.5 bg-gray-400 rounded-full"
                                            />
                                        </motion.div>
                                    </motion.div>
                                    <span className="text-xs text-gray-500 mt-1 block">
                                        Vincent is thinking
                                    </span>
                                </div>
                            )}
                        </AnimatePresence>
                    </>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Uploaded attachments indicator */}
            {uploadedDocIds.length > 0 && (
                <div className="px-4 pb-2">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                        <div className="text-[10px] font-medium text-blue-700 mb-1">
                            Attachments {uploadedDocIds.length}
                        </div>
                        <button
                            onClick={() => setUploadedDocIds([])}
                            className="text-[10px] text-blue-600 hover:text-blue-800">
                            Clear all
                        </button>
                    </div>
                </div>
            )}

            {/* Loading indicator */}
            {isSendingMessage && (
                <div className="px-4 pb-2">
                    <div className="text-xs text-gray-500 flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        Sending message...
                    </div>
                </div>
            )}

            {/* Input area */}
            <div className="px-4 pb-4 pt-2 border-t border-gray-100 flex-shrink-0 relative">
                {/* Slash command palette */}
                {showCommandPalette && filteredCommands.length > 0 && (
                    <div className="absolute bottom-full left-4 right-4 mb-1 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden z-10">
                        <div className="px-3 py-1.5 border-b border-gray-100 bg-gray-50">
                            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                                Actions
                            </span>
                        </div>
                        {filteredCommands.map((c) => (
                            <button
                                key={c.cmd}
                                onClick={() => {
                                    setInput(c.cmd);
                                    setShowCommandPalette(false);
                                    sendMessage(c.cmd);
                                }}
                                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-blue-50 transition-colors text-left group">
                                <span className="text-xs font-mono font-semibold text-blue-600 group-hover:text-blue-700 w-24 flex-shrink-0">
                                    {c.cmd}
                                </span>
                                <span className="text-xs text-gray-500 group-hover:text-gray-700 truncate flex-1">
                                    {c.description}
                                </span>
                                {c.path && (
                                    <svg
                                        className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-600 flex-shrink-0"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M13 7l5 5m0 0l-5 5m5-5H6"
                                        />
                                    </svg>
                                )}
                            </button>
                        ))}
                    </div>
                )}

                <div className="flex items-center gap-2 bg-gray-50 rounded-xl border border-gray-200 px-3 py-2 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-500/20 transition-all">
                    {/* File attach */}
                    <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading || !currentSessionId}
                        title={
                            !currentSessionId
                                ? "Send a message first to start a conversation"
                                : isUploading
                                  ? "Uploading..."
                                  : "Attach file"
                        }
                        className={`flex-shrink-0 transition-colors ${
                            isUploading || !currentSessionId
                                ? "text-gray-300 cursor-not-allowed"
                                : "text-gray-400 hover:text-blue-600"
                        }`}>
                        {isUploading ? (
                            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                                />
                            </svg>
                        )}
                    </motion.button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        onChange={handleFileAttach}
                        disabled={isUploading || !currentSessionId}
                    />

                    <textarea
                        value={input}
                        onChange={(e) => handleInputChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !showCommandPalette)
                                sendMessage(input);
                            if (e.key === "Escape")
                                setShowCommandPalette(false);
                        }}
                        placeholder={
                            "Ask me anything, or type / for commands..."
                        }
                        rows={1}
                        className="flex-1 h-10 min-w-0 resize-none rounded-xl !border-0 bg-transparent px-1 text-[12px]  !text-gray-600 placeholder-gray-400 shadow-none outline-none !ring-0 focus:!border-0 focus:outline-none focus:!ring-0"
                    />
                    <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        type="button"
                        onClick={toggleSpeechRecognition}
                        title={
                            isListening ? "Stop listening" : "Start voice input"
                        }
                        aria-label="Voice to text"
                        className={`flex items-center justify-center w-10 h-10 rounded-xl border transition-all flex-shrink-0 ${
                            isListening
                                ? "border-green-400 bg-green-50 text-green-600 shadow-sm"
                                : "border-gray-200 bg-white text-gray-500 hover:text-blue-600 hover:border-blue-300"
                        }`}>
                        <motion.svg
                            animate={isListening ? { scale: [1, 1.2, 1] } : {}}
                            transition={{ duration: 1, repeat: Infinity }}
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 5a3 3 0 00-3 3v4a3 3 0 106 0V8a3 3 0 00-3-3zm0 0v1m0 12v1m-4-2.5a7 7 0 008 0M5 11a7 7 0 0014 0"
                            />
                        </motion.svg>
                    </motion.button>
                    <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => sendMessage(input)}
                        disabled={
                            !input.trim() ||
                            isSendingMessage ||
                            isUploading ||
                            isCreatingSession
                        }
                        className="flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-[#2e5cb8] text-white disabled:opacity-40 hover:shadow-md transition-all flex-shrink-0">
                        {isSendingMessage ? (
                            <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                                />
                            </svg>
                        )}
                    </motion.button>
                </div>
                {isListening && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="mt-2 rounded-xl border border-green-200 bg-green-50 px-3 py-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                            <span className="text-xs font-medium text-green-700">
                                Listening... Speak now
                            </span>
                        </div>
                        <div className="flex items-end gap-1 h-5">
                            {[0, 1, 2, 3, 4].map((bar) => (
                                <motion.span
                                    key={bar}
                                    animate={{ scaleY: [1, 1.5, 1] }}
                                    transition={{
                                        duration: 0.5,
                                        repeat: Infinity,
                                        delay: bar * 0.1,
                                    }}
                                    className="w-1 h-3 rounded-full bg-green-500"
                                />
                            ))}
                        </div>
                    </motion.div>
                )}
                <p className="text-[10px] text-gray-400 mt-1.5 text-start">
                    Type / for commands
                </p>
                <p className="mt-5 text-xs text-gray-400 border-t pt-3">
                    Disclaimer: Vincent may occasionally make mistakes. Verify
                    critical information.
                </p>
            </div>
        </div>
    );
};

export default AIChatPanel;
